/**
 * Collapsible component handler with performance optimizations
 * Uses throttled resize handling, batched layout calculations, and height caching
 */
(function() {
  'use strict';

  // WeakMap to store element metadata without memory leaks
  const collapsibleData = new WeakMap();

  /**
   * Calculate transition duration based on content height
   * @param {number} height - Content height in pixels
   * @returns {number} Transition duration in seconds
   */
  const calculateDuration = (height) => {
    // Base duration of 0.3s for small content
    // Add 0.1s for every 100px of content height
    return Math.max(0.3, 0.3 + (height / 1000));
  };

  /**
   * Get or create cached data for a collapsible element
   * @param {HTMLElement} header - The collapsible header element
   * @returns {Object|null} Cached element data or null if invalid
   */
  const getCollapsibleData = (header) => {
    if (!header || !header.parentElement) {
      return null;
    }
    
    if (!collapsibleData.has(header)) {
      const container = header.parentElement;
      const answer = header.nextElementSibling;
      
      // Validate that answer element exists and has the correct class
      if (!answer || !answer.classList.contains('collapsible-content')) {
        return null;
      }
      
      const content = answer.querySelector('.collapsible-content-content');
      if (!content) {
        return null;
      }
      
      const minHeight = parseInt(container.dataset.minHeight || '0', 10);
      const closeOthers = container.dataset.closeOthers === 'true';
      
      // Find parent container with multiple collapsibles for closeOthers logic
      // Optimized: uses closest() when possible to avoid DOM traversal
      let parentContainer = null;
      if (closeOthers) {
        const commonParent = container.closest('[data-collapsible-group]');
        if (commonParent) {
          parentContainer = commonParent;
        } else {
          const collapsibleContainer = container.closest('.collapsible-container');
          if (collapsibleContainer) {
            parentContainer = collapsibleContainer;
          } else {
            // Fallback: traverse up the DOM tree
            let current = container.parentElement;
            while (current && current !== document.body) {
              const headers = current.querySelectorAll('.collapsible-header');
              if (headers.length > 1) {
                parentContainer = current;
                break;
              }
              current = current.parentElement;
            }
          }
        }
      }
      
      collapsibleData.set(header, {
        container,
        answer,
        content,
        minHeight,
        closeOthers,
        parentContainer,
        cachedHeight: null,
        cachedDuration: null
      });
    }
    
    return collapsibleData.get(header);
  };

  /**
   * Initialize collapsible elements and set initial state
   * Uses requestAnimationFrame to ensure DOM and styles are fully ready
   */
  const initCollapsibles = () => {
    requestAnimationFrame(() => {
      const questionHeaders = document.querySelectorAll('.collapsible-header');
      
      if (questionHeaders.length === 0) {
        return; // No collapsibles found
      }
      
      questionHeaders.forEach((header) => {
        const data = getCollapsibleData(header);
        if (!data || !data.content) {
          return;
        }
        
        const { answer, content, minHeight } = data;
        
        // Set initial state based on aria-expanded attribute
        const ariaExpanded = header.getAttribute('aria-expanded');
        const isExpanded = ariaExpanded === 'true' || ariaExpanded === true;
        
        if (isExpanded) {
          const contentHeight = content.offsetHeight;
          answer.style.height = `${contentHeight}px`;
          data.cachedHeight = contentHeight;
          data.cachedDuration = calculateDuration(contentHeight);
        } else {
          answer.style.height = `${minHeight}px`;
        }
        
        // Ensure button doesn't submit forms
        if (header.tagName === 'BUTTON') {
          header.type = 'button';
        }
        
        // Add click handler directly to each header
        header.addEventListener('click', function (event) {
          event.preventDefault();
          event.stopPropagation();
          
          const clickData = getCollapsibleData(this);
          if (!clickData || !clickData.content) {
            return;
          }
          
          const { answer, content, minHeight, closeOthers, parentContainer } = clickData;
          const currentAriaExpanded = this.getAttribute('aria-expanded');
          const isCurrentlyExpanded = currentAriaExpanded === 'true' || currentAriaExpanded === true;
          
          // Get content height (use cache if available and not expanding)
          let contentHeight = clickData.cachedHeight;
          if (contentHeight === null || isCurrentlyExpanded) {
            contentHeight = content.offsetHeight;
            clickData.cachedHeight = contentHeight;
          }
          
          // Get or calculate transition duration (use cache if available)
          let duration = clickData.cachedDuration;
          if (duration === null || isCurrentlyExpanded) {
            duration = calculateDuration(contentHeight);
            clickData.cachedDuration = duration;
          }
          
          // Update transition duration
          answer.style.transition = `height ${duration}s cubic-bezier(0.215, 0.61, 0.355, 1)`;
          
          // Toggle expanded state
          const newExpandedState = !isCurrentlyExpanded;
          this.setAttribute('aria-expanded', newExpandedState);
          
          // Handle closeOthers logic with cached parent container
          if (newExpandedState && closeOthers && parentContainer) {
            // Batch DOM reads for other headers
            const otherHeaders = Array.from(parentContainer.querySelectorAll('.collapsible-header'))
              .filter((h) => h !== this);
            
            // Batch DOM writes
            otherHeaders.forEach((otherHeader) => {
              const otherData = getCollapsibleData(otherHeader);
              if (otherData) {
                const otherAriaExpanded = otherHeader.getAttribute('aria-expanded');
                const otherIsExpanded = otherAriaExpanded === 'true' || otherAriaExpanded === true;
                
                if (otherIsExpanded) {
                  otherHeader.setAttribute('aria-expanded', 'false');
                  otherData.answer.style.height = `${otherData.minHeight}px`;
                  // Clear cache when closed
                  otherData.cachedHeight = null;
                  otherData.cachedDuration = null;
                }
              }
            });
          }
          
          // Toggle height with smooth animation
          if (newExpandedState) {
            answer.style.height = `${contentHeight}px`;
          } else {
            answer.style.height = `${minHeight}px`;
            // Clear cache when closed
            clickData.cachedHeight = null;
            clickData.cachedDuration = null;
          }
        });
      });
    });
  };

  /**
   * Update height for expanded collapsibles during resize
   * Batched using requestAnimationFrame to minimize layout thrashing
   */
  let resizeAnimationFrame = null;
  const updateResizedCollapsibles = () => {
    if (resizeAnimationFrame) {
      return; // Already scheduled
    }
    
    resizeAnimationFrame = requestAnimationFrame(() => {
      // Batch all DOM reads first
      const expandedHeaders = Array.from(
        document.querySelectorAll('.collapsible-header[aria-expanded="true"]')
      );
      
      const updates = expandedHeaders.map((header) => {
        const data = getCollapsibleData(header);
        if (!data || !data.content) return null;
        
        return {
          answer: data.answer,
          content: data.content,
          height: data.content.offsetHeight,
          data
        };
      }).filter(Boolean);
      
      // Batch all DOM writes
      updates.forEach(({ answer, height, data }) => {
        const duration = calculateDuration(height);
        answer.style.transition = `height ${duration}s cubic-bezier(0.215, 0.61, 0.355, 1)`;
        answer.style.height = `${height}px`;
        
        // Update cache
        data.cachedHeight = height;
        data.cachedDuration = duration;
      });
      
      resizeAnimationFrame = null;
    });
  };

  /**
   * Throttled resize handler using requestAnimationFrame
   * Prevents excessive calculations during window resizing
   */
  window.addEventListener('resize', updateResizedCollapsibles);

  /**
   * Initialize collapsibles with retry mechanism
   * Handles cases where DOM might not be fully ready
   */
  const initWithRetry = () => {
    const headers = document.querySelectorAll('.collapsible-header');
    
    if (headers.length === 0) {
      // Retry after a short delay if headers aren't found yet
      setTimeout(initWithRetry, 100);
      return;
    }
    
    initCollapsibles();
  };

  // Initialize collapsibles when DOM is ready
  // Since script uses defer, DOM parsing is complete but styles may not be loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWithRetry);
  } else {
    // DOM is interactive or complete - use setTimeout to ensure styles are applied
    setTimeout(initWithRetry, 0);
  }
})();
